var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Big from "big.js";
import { Bitcoin, ExchangeRate, MonetaryAmount } from "@interlay/monetary-js";
import { ATOMIC_UNIT, convertMoment, createExchangeRateOracleKey, createFeeEstimationOracleKey, decodeFixedPointType, encodeUnsignedFixedPoint, isCurrencyEqual, storageKeyToNthInner, unwrapRawExchangeRate, } from "../utils";
import { currencyIdToMonetaryCurrency, isLendToken } from "../utils/currency";
import { newCurrencyId } from "../utils/encoding";
export class DefaultOracleAPI {
    constructor(api, wrappedCurrency) {
        this.api = api;
        this.wrappedCurrency = wrappedCurrency;
    }
    getExchangeRate(currency) {
        return __awaiter(this, void 0, void 0, function* () {
            // KBTC / IBTC have an exchange rate of one
            if (isCurrencyEqual(currency, this.wrappedCurrency)) {
                return new ExchangeRate(currency, currency, new Big(1));
            }
            if (isLendToken(currency)) {
                const lendTokenId = newCurrencyId(this.api, currency);
                const underlyingCurrencyIdRaw = yield this.api.query.loans.underlyingAssetId(lendTokenId);
                if (underlyingCurrencyIdRaw.isNone) {
                    throw new Error(`No underlying asset found for lend token with id ${lendTokenId.asLendToken.toString()}`);
                }
                const underlyingCurrencyId = underlyingCurrencyIdRaw.unwrap();
                const rawLendToUnderlying = yield this.api.query.loans.exchangeRate(underlyingCurrencyId);
                // multiply this rate with lendtoken to get underlying amount
                const lendToUnderRate = decodeFixedPointType(rawLendToUnderlying);
                const underlyingCurrency = yield currencyIdToMonetaryCurrency(this.api, underlyingCurrencyIdRaw.unwrap());
                const btcToUnderRate = (yield this.convertWrappedToCurrency(new MonetaryAmount(Bitcoin, 1), underlyingCurrency)).toBig();
                const btcToLendRate = btcToUnderRate.div(lendToUnderRate);
                // final rate is normalized (base unit vs base unit), construct accordingly
                return new ExchangeRate(this.wrappedCurrency, currency, btcToLendRate);
            }
            const oracleKey = createExchangeRateOracleKey(this.api, currency);
            const encodedRawRate = unwrapRawExchangeRate(yield this.api.query.oracle.aggregate(oracleKey));
            if (encodedRawRate === undefined) {
                return Promise.reject(new Error(`No exchange rate for given currency: ${currency.ticker}`));
            }
            const decodedRawRate = decodeFixedPointType(encodedRawRate);
            return new ExchangeRate(this.wrappedCurrency, currency, decodedRawRate, ATOMIC_UNIT, ATOMIC_UNIT);
        });
    }
    convertWrappedToCurrency(amount, currency) {
        return __awaiter(this, void 0, void 0, function* () {
            const rate = yield this.getExchangeRate(currency);
            return rate.toCounter(amount);
        });
    }
    convertCollateralToWrapped(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const rate = yield this.getExchangeRate(amount.currency);
            return rate.toBase(amount);
        });
    }
    getOnlineTimeout() {
        return __awaiter(this, void 0, void 0, function* () {
            const moment = yield this.api.query.oracle.maxDelay();
            return moment.toNumber();
        });
    }
    setExchangeRate(exchangeRate) {
        const encodedExchangeRate = encodeUnsignedFixedPoint(this.api, exchangeRate.toBig([ATOMIC_UNIT, ATOMIC_UNIT]));
        const oracleKey = createExchangeRateOracleKey(this.api, exchangeRate.counter);
        const tx = this.api.tx.oracle.feedValues([[oracleKey, encodedExchangeRate]]);
        return { extrinsic: tx, event: this.api.events.oracle.FeedValues };
    }
    getBitcoinFees() {
        return __awaiter(this, void 0, void 0, function* () {
            const oracleKey = createFeeEstimationOracleKey(this.api);
            const fees = yield this.api.query.oracle.aggregate(oracleKey);
            const parseFees = (fee) => {
                const inner = unwrapRawExchangeRate(fee);
                if (inner !== undefined) {
                    return decodeFixedPointType(inner);
                }
                throw new Error("Bitcoin fee estimate not set");
            };
            return parseFees(fees);
        });
    }
    setBitcoinFees(fees) {
        if (!fees.round().eq(fees)) {
            throw new Error("tx fees must be an integer amount of satoshi");
        }
        else if (fees.lt(0)) {
            throw new Error("tx fees must be a positive amount of satoshi");
        }
        const oracleKey = createFeeEstimationOracleKey(this.api);
        const encodedFee = encodeUnsignedFixedPoint(this.api, fees);
        const tx = this.api.tx.oracle.feedValues([[oracleKey, encodedFee]]);
        return { extrinsic: tx, event: this.api.events.oracle.FeedValues };
    }
    getSourcesById() {
        return __awaiter(this, void 0, void 0, function* () {
            const oracles = yield this.api.query.oracle.authorizedOracles.entries();
            const nameMap = new Map();
            oracles.forEach((oracle) => nameMap.set(storageKeyToNthInner(oracle[0]).toString(), oracle[1].toUtf8()));
            return nameMap;
        });
    }
    getValidUntil(counterCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const oracleKey = createExchangeRateOracleKey(this.api, counterCurrency);
            const validUntil = yield this.api.query.oracle.validUntil(oracleKey);
            return validUntil.isSome ? convertMoment(validUntil.value) : Promise.reject("No such oracle key");
        });
    }
    isOnline(currency) {
        return __awaiter(this, void 0, void 0, function* () {
            const oracleKey = createExchangeRateOracleKey(this.api, currency);
            const value = yield this.api.query.oracle.aggregate(oracleKey);
            // If the oracle has value then it is active for the given currency.
            return value.isSome;
        });
    }
    getRawValuesUpdated(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const isSet = yield this.api.query.oracle.rawValuesUpdated(key);
            return isSet.unwrap().isTrue;
        });
    }
}
//# sourceMappingURL=oracle.js.map